import { useEffect, useMemo, useState } from 'react'

import type {
  Conversation,
  TraceStep,
  TraceSummary,
} from '../components/flowDebugger/types'
import {
  traceService,
  type ObservabilityAnalysisProvider,
  type ObservabilityAnalysisRequest,
  type ObservabilityAnalysisResponse,
} from '../services/traceService'
import DebugAnalysisResultPanel from './DebugAnalysisResultPanel'
import { useAIAnalysisModels } from './useAIAnalysisModels'

type DebugTraceAnalysisPanelProps = {
  tenantId: string
  conversationId: string
  trace: TraceStep[]
  historyTraces: TraceSummary[]
  selectedTraceSummary: TraceSummary | null
  selectedConversation: Conversation | null
  activeTraceId: string | null
  liveMode: boolean
  loadingTrace: boolean
}

type DebugTraceAnalysisResult = ObservabilityAnalysisResponse['data']

const scopeLabel = 'Trace Inspector'
const controlStyle = {
  width: '100%',
  padding: '10px 10px',
  borderRadius: 10,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.14)',
  color: '#e2e8f0',
  fontSize: 13,
}

function pickStepData(step: TraceStep) {
  const data = step.data ?? {}
  return {
    stage: data.stage ?? null,
    route: data.route ?? null,
    finalRoute: data.finalRoute ?? null,
    authoritySource: data.authoritySource ?? null,
    errorCode: data.errorCode ?? null,
    cacheHit: data.cacheHit ?? null,
    inputTokens: data.inputTokens ?? null,
    outputTokens: data.outputTokens ?? null,
    shouldEscalate: data.shouldEscalate ?? null,
    checkpointId: data.checkpointId ?? null,
    checkpointSizeBytes: data.checkpointSizeBytes ?? null,
    hitlRequest: data.hitlRequest ? true : null,
  }
}

function truncatePreview(value: unknown, limit = 480) {
  if (value == null) return null
  const raw = typeof value === 'string' ? value : JSON.stringify(value)
  if (raw.length <= limit) return raw
  return `${raw.slice(0, limit)}...`
}

function buildToolPreview(steps: TraceStep[]) {
  return steps
    .filter((step) => step.type === 'TOOL_EXECUTION' || step.type === 'TOOL_CALL' || step.type === 'CACHE')
    .slice(-6)
    .map((step) => ({
      type: step.type,
      ms: step.ms,
      tool: step.data?.tool || step.data?.name || null,
      cacheHit: step.data?.cacheHit ?? null,
      error: step.data?.error || step.data?.errorCode || null,
      resultPreview: truncatePreview(step.data?.result ?? step.data?.output ?? step.data?.response),
      argsPreview: truncatePreview(step.data?.args ?? step.data?.input),
    }))
}

function buildFinalResponsePreview(steps: TraceStep[]) {
  const lastRespond = [...steps].reverse().find((step) => step.type === 'RESPOND' || step.type === 'LLM')
  if (!lastRespond) return null
  if (lastRespond.type === 'RESPOND') {
    return truncatePreview(lastRespond.data?.message ?? lastRespond.data?.response ?? null)
  }
  return truncatePreview(lastRespond.data?.rawResponse ?? lastRespond.data?.message ?? null)
}

export function buildDebugAnalysisRequest(input: {
  tenantId: string
  conversationId: string
  trace: TraceStep[]
  historyTraces: TraceSummary[]
  selectedTraceSummary: TraceSummary | null
  selectedConversation: Conversation | null
  activeTraceId: string | null
  liveMode: boolean
}): ObservabilityAnalysisRequest {
  const traceSummary = input.selectedTraceSummary
  const durationSeconds = Math.max(1, Math.ceil((traceSummary?.totalDurationMs ?? 0) / 1000))
  const historyPreview = input.historyTraces.slice(0, 8).map((trace) => ({
    id: trace.id,
    strategy: trace.strategy,
    createdAt: trace.createdAt,
    totalDurationMs: trace.totalDurationMs,
    shouldEscalate: trace.shouldEscalate,
    finalAction: trace.finalAction ?? null,
  }))
  const stepSamples = input.trace.slice(-18).map((step, index) => ({
    index,
    type: step.type,
    ms: step.ms,
    data: pickStepData(step),
  }))
  const tracePreview = stepSamples
  const toolPreview = buildToolPreview(input.trace)
  const finalResponsePreview = buildFinalResponsePreview(input.trace)

  return {
    scope: 'debug',
    tenantId: input.tenantId,
    timeframe: `${durationSeconds}s trace`,
    summary: {
      traceId: traceSummary?.id ?? null,
      message: traceSummary?.message ?? null,
      messageId: traceSummary?.messageId ?? null,
      strategy: traceSummary?.strategy ?? null,
      totalDurationMs: traceSummary?.totalDurationMs ?? null,
      totalTokensEst: traceSummary?.totalTokensEst ?? null,
      shouldEscalate: traceSummary?.shouldEscalate ?? null,
      finalAction: traceSummary?.finalAction ?? null,
      complexity: traceSummary?.complexity ?? null,
      complexityReason: traceSummary?.complexityReason ?? null,
      intentTrajectory: traceSummary?.intentTrajectory ?? null,
      stepCount: input.trace.length,
      liveMode: input.liveMode,
      activeTraceId: input.activeTraceId,
      historyTraceCount: input.historyTraces.length,
      selectedConversation: input.selectedConversation
        ? {
            id: input.selectedConversation.id,
            contactName: input.selectedConversation.contactName,
            waContact: input.selectedConversation.waContact,
            status: input.selectedConversation.status,
            mode: input.selectedConversation.mode,
          }
        : null,
      historyPreview,
    },
    samples: undefined,
    tracePreview,
    toolPreview: toolPreview.length > 0 ? toolPreview : undefined,
    coherenceEvidence: {
      userIntentPreview: traceSummary?.message ?? null,
      finalResponsePreview,
      finalAction: traceSummary?.finalAction ?? null,
      toolPreviewCount: toolPreview.length,
      intentTrajectory: traceSummary?.intentTrajectory ?? null,
    },
    effectivePolicy: {
      activeTraceId: input.activeTraceId,
      selectedConversationId: input.selectedConversation?.id ?? null,
      selectedConversationStatus: input.selectedConversation?.status ?? null,
      isEscalated: Boolean(traceSummary?.shouldEscalate),
      liveMode: input.liveMode,
      historyTraceCount: input.historyTraces.length,
      coherenceEvidencePresent: Boolean(traceSummary?.message || finalResponsePreview || toolPreview.length > 0),
      intentTrajectoryPatterns: traceSummary?.intentTrajectory?.patterns ?? [],
    },
    observability: {
      scope: 'debug',
      windowMinutes: 60,
      activeConversations: input.selectedConversation ? 1 : 0,
      activeLongConversations: traceSummary && traceSummary.totalDurationMs >= 60000 ? 1 : 0,
      generatedAt: new Date().toISOString(),
    },
    debug: true,
  }
}

export function DebugTraceAnalysisPanelView({
  tenantId,
  conversationId,
  provider,
  model,
  modelOptions,
  defaultModel,
  modelsLoading,
  modelsError,
  resolvedProvider,
  loading,
  error,
  result,
  traceCount,
  traceReady,
  onProviderChange,
  onModelChange,
  onAnalyze,
}: {
  tenantId: string
  conversationId: string
  provider: ObservabilityAnalysisProvider
  model: string
  modelOptions: string[]
  defaultModel: string
  modelsLoading: boolean
  modelsError: string
  resolvedProvider: ObservabilityAnalysisProvider
  loading: boolean
  error: string
  result: DebugTraceAnalysisResult | null
  traceCount: number
  traceReady: boolean
  onProviderChange: (value: ObservabilityAnalysisProvider) => void
  onModelChange: (value: string) => void
  onAnalyze: () => void
}) {
  const hasResult = Boolean(result)
  const modelListId = `analysis-models-${conversationId || 'trace'}`
  const catalogModels = Array.from(new Set([defaultModel, ...modelOptions].filter(Boolean)))
  const selectedCatalogModel = catalogModels.includes(model.trim()) ? model.trim() : (defaultModel || modelOptions[0] || '')

  return (
    <section
      id="ai-analysis"
      style={{
        margin: '0 20px 16px 20px',
        border: '1px solid rgba(59,130,246,0.18)',
        borderRadius: 18,
        background: 'linear-gradient(180deg, rgba(59,130,246,0.08), rgba(255,255,255,0.02))',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 900 }}>Analyze with AI</div>
            <span
              style={{
                display: 'inline-flex',
                padding: '3px 8px',
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.04)',
                color: 'rgba(226,232,240,0.78)',
                fontSize: 11,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            >
              {scopeLabel}
            </span>
            <span
              style={{
                display: 'inline-flex',
                padding: '3px 8px',
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.04)',
                color: 'rgba(226,232,240,0.78)',
                fontSize: 11,
              }}
            >
              tenant={tenantId || '-'}
            </span>
            <span
              style={{
                display: 'inline-flex',
                padding: '3px 8px',
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.04)',
                color: 'rgba(226,232,240,0.78)',
                fontSize: 11,
              }}
            >
              conversation={conversationId || '-'}
            </span>
            <span
              style={{
                display: 'inline-flex',
                padding: '3px 8px',
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.04)',
                color: 'rgba(226,232,240,0.78)',
                fontSize: 11,
              }}
            >
              steps={traceCount}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.65)', marginTop: 4 }}>
            Resume el trace actual y devuelve hallazgos accionables sin tocar el runtime.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select
            style={{ ...controlStyle, width: 140 }}
            value={provider}
            onChange={(e) => onProviderChange(e.target.value as ObservabilityAnalysisProvider)}
            disabled={!tenantId || loading}
          >
            <option value="auto">auto</option>
              <option value="openai">openai</option>
              <option value="anthropic">anthropic</option>
              <option value="mistral">mistral</option>
              <option value="deepseek">deepseek</option>
              <option value="gemini">gemini</option>
            </select>
          <select
            style={{ ...controlStyle, width: 240 }}
            value={selectedCatalogModel}
            onChange={(e) => onModelChange(e.target.value)}
            disabled={!tenantId || loading || modelsLoading || catalogModels.length === 0}
          >
            <option value="">
              {modelsLoading
                ? 'Loading models...'
                : catalogModels.length > 0
                  ? `Default (${defaultModel || catalogModels[0]})`
                  : 'No catalog available'}
            </option>
            {catalogModels.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <input
            style={{ ...controlStyle, width: 180 }}
            value={model}
            onChange={(e) => onModelChange(e.target.value)}
            disabled={!tenantId || loading}
            placeholder="Custom override"
            list={modelListId}
          />
          <datalist id={modelListId}>
            {catalogModels.map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
          <button
            onClick={onAnalyze}
            disabled={!tenantId || !conversationId || !traceReady || loading}
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid rgba(59,130,246,0.34)',
              background: loading ? 'rgba(255,255,255,0.04)' : 'linear-gradient(135deg, rgba(59,130,246,0.95), rgba(16,185,129,0.85))',
              color: '#fff',
              fontSize: 12,
              fontWeight: 900,
              cursor: !tenantId || !conversationId || !traceReady || loading ? 'not-allowed' : 'pointer',
              opacity: !tenantId || !conversationId || !traceReady || loading ? 0.65 : 1,
            }}
          >
            {loading ? 'Analizando...' : 'Analyze with AI'}
          </button>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ marginBottom: 10, fontSize: 11, color: 'rgba(226,232,240,0.65)' }}>
          {modelsLoading
            ? `Cargando modelos para ${resolvedProvider}...`
            : modelOptions.length > 0
              ? `Modelos cargados para ${resolvedProvider}: ${modelOptions.join(', ')}`
              : modelsError || 'Puedes escribir un modelo manualmente si quieres forzar uno concreto.'}
        </div>
        {!tenantId ? (
          <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
            Selecciona un tenant para analizar el trace.
          </div>
        ) : !conversationId ? (
          <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
            Selecciona una conversacion para analizar su trazado.
          </div>
        ) : loading ? (
          <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
            Generando analisis y resumiendo el trace actual...
          </div>
        ) : error ? (
          <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(239,68,68,0.34)', background: 'rgba(239,68,68,0.08)', color: '#fecaca' }}>
            {error}
          </div>
        ) : !traceReady ? (
          <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
            Aun no hay un trace listo para analizar. Abre una conversacion con historial o espera a que cargue el ultimo trace.
          </div>
        ) : !hasResult ? (
          <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.03)' }}>
            Pulsa <b>Analyze with AI</b> para generar un resumen ejecutivo, hallazgos, riesgos y recomendaciones sobre el trace actual.
          </div>
        ) : (
          <DebugAnalysisResultPanel result={result!} />
        )}
      </div>
    </section>
  )
}

export default function DebugTraceAnalysisPanel({
  tenantId,
  conversationId,
  trace,
  historyTraces,
  selectedTraceSummary,
  selectedConversation,
  activeTraceId,
  liveMode,
  loadingTrace,
}: DebugTraceAnalysisPanelProps) {
  const [provider, setProvider] = useState<ObservabilityAnalysisProvider>('auto')
  const [model, setModel] = useState('')
  const [result, setResult] = useState<DebugTraceAnalysisResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { models, defaultModel, loadingModels, modelsError, resolvedProvider } = useAIAnalysisModels(provider)
  const effectiveModel = model.trim() || defaultModel || models[0] || ''

  useEffect(() => {
    if (!model.trim() && !loadingModels && (defaultModel || models[0])) {
      setModel(defaultModel || models[0] || '')
    }
  }, [defaultModel, loadingModels, model, models])

  useEffect(() => {
    setResult(null)
    setError('')
  }, [tenantId, conversationId, activeTraceId, trace.length, selectedTraceSummary?.id])

  useEffect(() => {
    setModel('')
  }, [provider])

  const traceReady = useMemo(() => trace.length > 0 && !loadingTrace, [trace.length, loadingTrace])

  const analyze = async () => {
    if (!tenantId || !conversationId || !traceReady) return
    setLoading(true)
    setError('')
    try {
      const payload = buildDebugAnalysisRequest({
        tenantId,
        conversationId,
        trace,
        historyTraces,
        selectedTraceSummary,
        selectedConversation,
        activeTraceId,
        liveMode,
      })
      const response = await traceService.analyzeObservability({
        ...payload,
        provider,
        ...(effectiveModel ? { model: effectiveModel } : {}),
      })
      setResult(response.data)
    } catch (err) {
      setResult(null)
      setError(err instanceof Error ? err.message : 'No se pudo analizar el trace actual')
    } finally {
      setLoading(false)
    }
  }

  return (
    <DebugTraceAnalysisPanelView
      tenantId={tenantId}
      conversationId={conversationId}
      provider={provider}
      model={effectiveModel}
      modelOptions={models}
      defaultModel={defaultModel}
      modelsLoading={loadingModels}
      modelsError={modelsError}
      resolvedProvider={resolvedProvider}
      loading={loading}
      error={error}
      result={result}
      traceCount={trace.length}
      traceReady={traceReady}
      onProviderChange={setProvider}
      onModelChange={setModel}
      onAnalyze={analyze}
    />
  )
}


